import { useState, useEffect, useCallback } from 'react';
import { firmsApi, type BrokerMoment, type AcknowledgeResult } from '../lib/api';

/** Compact reference for a UUID — first 8 chars (the full id is metadata-only anyway). */
function shortId(id: string): string {
  return id && id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

/** Format an ISO timestamp for display; fall back to the raw string if unparseable. */
function formatTime(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** A broker moment the firm acknowledged this session, paired with its sealed routing. */
interface Acknowledged {
  moment: BrokerMoment;
  result: AcknowledgeResult;
}

export default function BrokerMomentsPage() {
  // Open feed (acknowledgeable requests on the firm's subscribed postcodes).
  const [moments, setMoments] = useState<BrokerMoment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row acknowledge state.
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [acknowledged, setAcknowledged] = useState<Acknowledged[]>([]);

  const loadMoments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await firmsApi.getBrokerMoments();
      setMoments(data.open_requests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load broker moments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMoments();
  }, [loadMoments]);

  async function handleAcknowledge(moment: BrokerMoment) {
    setAcknowledgingId(moment.id);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[moment.id];
      return next;
    });
    try {
      const result = await firmsApi.acknowledge(moment.id);
      setAcknowledged((prev) => [{ moment, result }, ...prev]);
      // The acknowledged request is no longer open — refresh so it leaves the feed.
      await loadMoments();
    } catch (err) {
      // 409 (already acknowledged) / 403 (not subscribed) are expected round-robin
      // outcomes — surface the message on the row, never crash.
      setRowErrors((prev) => ({
        ...prev,
        [moment.id]: err instanceof Error ? err.message : 'Failed to acknowledge',
      }));
    } finally {
      setAcknowledgingId(null);
    }
  }

  return (
    <div className="min-h-screen">
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Broker Moments</h1>
          <p className="text-sm text-gray-500">
            Open requests on your subscribed postcodes. Acknowledge to take the routing.
          </p>
        </div>

        {/* Open feed */}
        <section className="bg-white rounded-boby-lg border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">Open requests</h2>
            <button
              type="button"
              onClick={() => void loadMoments()}
              disabled={loading}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 underline disabled:opacity-60"
            >
              Refresh
            </button>
          </div>

          {loading && <p className="text-sm text-gray-500">Loading open requests…</p>}

          {!loading && error && (
            <div className="space-y-2">
              <p role="alert" className="text-sm text-red-600">{error}</p>
              <button
                type="button"
                onClick={() => void loadMoments()}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 underline"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && moments.length === 0 && (
            <p className="text-sm text-gray-500">No open requests.</p>
          )}

          {!loading && !error && moments.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {moments.map((m) => (
                <li key={m.id} className="py-4 space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-sm flex-1">
                      <Field label="Postcode" value={m.postcode} />
                      <Field label="Country" value={m.country_code} />
                      <Field label="Filed" value={formatTime(m.created_at)} />
                      <Field label="Memo" value={shortId(m.memo_id)} />
                    </dl>
                    <button
                      type="button"
                      onClick={() => void handleAcknowledge(m)}
                      disabled={acknowledgingId === m.id}
                      className="shrink-0 rounded-boby-sm bg-boby-gold px-4 py-2 font-semibold text-gray-900 transition hover:brightness-95 disabled:opacity-60"
                    >
                      {acknowledgingId === m.id ? 'Acknowledging…' : 'Acknowledge'}
                    </button>
                  </div>
                  {rowErrors[m.id] && (
                    <p role="alert" className="text-sm text-red-600">{rowErrors[m.id]}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Sealed routings from acknowledgements made this session. The acknowledged
            request leaves the open feed above (it is no longer pending), so its sealed
            routing is shown here so the result stays visible. */}
        {acknowledged.length > 0 && (
          <section className="bg-white rounded-boby-lg border border-gray-100 p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Acknowledged — routing sealed</h2>
            <ul className="space-y-3">
              {acknowledged.map(({ moment, result }) => (
                <li
                  key={result.routing_id}
                  className="rounded-boby-sm border border-green-200 bg-green-50 p-4"
                >
                  <p className="text-sm font-medium text-green-800 mb-2">
                    Postcode {moment.postcode} — legal-lane routing sealed
                  </p>
                  <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-sm">
                    <Field label="Routing" value={shortId(result.routing_id)} />
                    <Field label="Asset" value={shortId(result.asset_id)} />
                    <Field label="Lane" value={result.lane} />
                    <Field label="Sealed" value={formatTime(result.sealed_at)} />
                  </dl>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-gray-400">{label}</dt>
      <dd className="text-gray-800">{value}</dd>
    </div>
  );
}
